/**
 * AddDailyEntryDialog Component
 * Dialog for contractors to create daily diary entries with photos
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  CalendarToday as CalendarIcon,
  AutoAwesome
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import FileUploadZone from '../FileUpload/FileUploadZone';
import DialogCloseButton from '../common/DialogCloseButton';
import projectService from '../../services/projectService';
import aiService from '../../services/aiService';

const AddDailyEntryDialog = ({
  open,
  onClose,
  projectId,
  project = null,
  milestones = [],
  onEntryCreated
}) => {
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [entryDate, setEntryDate] = useState(new Date());
  const [description, setDescription] = useState('');
  const [milestoneId, setMilestoneId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState([]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setEntryDate(new Date());
      setDescription('');
      setMilestoneId('');
      setSelectedFiles([]);
      setUploadProgress({});
      setErrors([]);
      setAiLoading(false);
    }
  }, [open]);

  // Validate form
  const validateForm = () => {
    const newErrors = [];

    if (!description || description.trim().length < 10) {
      newErrors.push('Description must be at least 10 characters');
    }

    if (description && description.length > 1000) {
      newErrors.push('Description cannot exceed 1000 characters');
    }

    if (selectedFiles.length === 0) {
      newErrors.push('At least one photo or video is required');
    }

    if (selectedFiles.length > 5) {
      newErrors.push('Maximum 5 files allowed');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // Handle file selection
  const handleFilesSelected = (files) => {
    // Accept images and videos
    const mediaFiles = files.filter(file => file.type.startsWith('image/') || file.type.startsWith('video/'));

    if (mediaFiles.length < files.length) {
      setErrors(['Only photo or video files are allowed for diary entries']);
      return;
    }

    // Limit to 5 files total
    const totalFiles = selectedFiles.length + mediaFiles.length;
    if (totalFiles > 5) {
      const availableSlots = Math.max(5 - selectedFiles.length, 0);
      if (availableSlots === 0) {
        setErrors(['You already selected 5 files. Remove one before adding another.']);
        return;
      }

      setSelectedFiles([...selectedFiles, ...mediaFiles.slice(0, availableSlots)]);
      setErrors([`You can upload up to 5 files. We added the first ${availableSlots} and skipped ${mediaFiles.length - availableSlots}.`]);
    } else {
      setSelectedFiles([...selectedFiles, ...mediaFiles]);
      setErrors([]);
    }
  };

  // Handle file removal
  const handleFileRemoved = (fileToRemove) => {
    setSelectedFiles(selectedFiles.filter(file => file !== fileToRemove));
  };

  // Handle camera capture (Capacitor)
  const handleCameraCapture = async () => {
    try {
      // Check if Capacitor Camera is available
      if (window.Capacitor && window.Capacitor.Plugins.Camera) {
        const { Camera } = window.Capacitor.Plugins;
        const photo = await Camera.getPhoto({
          resultType: 'Uri',
          source: 'Camera',
          quality: 80
        });

        // Convert to file
        const response = await fetch(photo.webPath);
        const blob = await response.blob();
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });

        handleFilesSelected([file]);
      } else {
        // Fallback for web - trigger file input with camera
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.capture = 'camera';
        input.onchange = (e) => {
          const files = Array.from(e.target.files);
          handleFilesSelected(files);
        };
        input.click();
      }
    } catch (error) {
      console.error('Camera capture error:', error);
      setErrors(['Failed to capture photo']);
    }
  };

  const handleImproveDescription = async () => {
    const currentDescription = description.trim();

    if (!currentDescription) {
      setErrors(['Add a rough progress note first, then AI can improve it.']);
      return;
    }

    setAiLoading(true);
    setErrors([]);

    try {
      const selectedMilestone = milestones.find(m => String(m.id) === String(milestoneId));
      const response = await aiService.improveProjectUpdateDescription({
        description: currentDescription,
        projectContext: {
          projectTitle: project?.title || project?.name,
          projectType: project?.project_type || project?.type,
          location: project?.location,
          milestoneName: selectedMilestone?.name,
          currentProgress: project?.progress_percentage || project?.progressPercentage
        }
      });

      if (!response?.improved) {
        throw new Error('AI did not return an improved update');
      }

      setDescription(response.improved);
    } catch (error) {
      console.error('Improve daily update error:', error);
      setErrors([error.response?.data?.error || error.message || 'Failed to improve update with AI']);
    } finally {
      setAiLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Upload media first (photos and/or videos)
      setErrors(['Uploading media...']);
      const uploadedFiles = await projectService.uploadDiaryPhotos(projectId, selectedFiles);

      if (!uploadedFiles || uploadedFiles.length === 0) {
        throw new Error('Failed to upload media');
      }

      // Extract file URLs
      const photoUrls = uploadedFiles.map(file => file.fileUrl);

      // Create diary entry
      const entryData = {
        entryDate: entryDate.toISOString().split('T')[0],
        description: description.trim(),
        photoUrls,
        milestoneId: milestoneId || null
      };

      const entry = await projectService.createDiaryEntry(projectId, entryData);

      setErrors([]);

      // Notify parent
      if (onEntryCreated) {
        onEntryCreated(entry);
      }

      // Close dialog
      onClose();
    } catch (error) {
      console.error('Create diary entry error:', error);
      setErrors([error.message || 'Failed to post daily update']);
    } finally {
      setLoading(false);
    }
  };

  // Character count
  const charCount = description.length;
  const charCountColor = charCount < 10 ? 'error' : charCount > 1000 ? 'error' : 'text.secondary';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogCloseButton onClick={onClose} />
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
          color: '#fff'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add Daily Update
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        {/* Errors */}
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        {/* Date Picker */}
        <Box sx={{ mb: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Entry Date"
              value={entryDate}
              onChange={(newDate) => setEntryDate(newDate)}
              maxDate={new Date()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  helperText="Cannot select future dates"
                />
              )}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: 'Cannot select future dates'
                }
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={aiLoading ? <CircularProgress size={16} /> : <AutoAwesome />}
              onClick={handleImproveDescription}
              disabled={loading || aiLoading || !description.trim()}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {aiLoading ? 'Improving...' : 'Improve with AI'}
            </Button>
          </Box>
          <TextField
            label="Progress Description"
            multiline
            rows={4}
            fullWidth
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe today's progress... (10-1000 characters)"
            helperText={
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>What work was completed today?</span>
                <span style={{ color: charCountColor }}>
                  {charCount}/1000 characters
                </span>
              </Box>
            }
            error={charCount < 10 || charCount > 1000}
          />
        </Box>

        {/* Milestone Selector (Optional) */}
        {milestones.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Link to Milestone (Optional)</InputLabel>
              <Select
                value={milestoneId}
                onChange={(e) => setMilestoneId(e.target.value)}
                label="Link to Milestone (Optional)"
              >
                <MenuItem value="">
                  <em>No specific milestone</em>
                </MenuItem>
                {milestones.map((milestone) => (
                  <MenuItem key={milestone.id} value={milestone.id}>
                    {milestone.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Camera Button (Mobile Priority) */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CameraIcon />}
            onClick={handleCameraCapture}
            disabled={loading || selectedFiles.length >= 5}
            fullWidth
          >
            Take Photo
          </Button>
        </Box>

        {/* File Upload Zone */}
        <FileUploadZone
          onFilesSelected={handleFilesSelected}
          onFileRemoved={handleFileRemoved}
          maxFiles={5}
          maxFileSize={100 * 1024 * 1024}
          acceptedTypes={{
            'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
            'video/*': ['.mp4', '.mov', '.webm']
          }}
          uploadedFiles={selectedFiles.map((file, index) => ({
            name: file.name,
            size: file.size,
            type: file.type,
            preview: URL.createObjectURL(file),
            id: index
          }))}
          uploadProgress={uploadProgress}
          errors={errors}
          allowCamera={false} // We have dedicated camera button
          allowVideo={true}
          title="Upload Progress Photos & Videos"
          description={`Select 1-5 photos or videos (${selectedFiles.length}/5 selected) - Required`}
        />

        {/* Media count indicator */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            📎 {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
            {selectedFiles.length === 0 && ' - At least 1 required'}
            {selectedFiles.length >= 5 && ' - Maximum reached'}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || selectedFiles.length === 0 || charCount < 10 || charCount > 1000}
          startIcon={loading && <CircularProgress size={20} />}
          sx={{
            background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
            color: '#fff'
          }}
        >
          {loading ? 'Posting...' : 'Post Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDailyEntryDialog;
